import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import { StreamingHelperService } from "../streaming-helper-service";
import { IDesignerLiveEvent } from "./designer-live-types";

// One SSE stream per designer tab for tool change events + project presence rosters
// (fetch-based like every stream here — native EventSource can't carry the Bearer header;
// board-events-stream's fixed 3s reconnect loop). Holding the stream open IS this tab's
// project presence; the server sweeps a 30s reconnect grace on drops.

const RECONNECT_DELAY_MS: number = 3000;

export interface IDesignerStreamHandle {
  stop: () => void;
}

function isDesignerLiveEvent(data: unknown): data is IDesignerLiveEvent {
  return typeof data === "object" && data !== null && typeof (data as { type?: unknown }).type === "string";
}

export function connectDesignerEventStream(
  clientId: string,
  onEvent: (liveEvent: IDesignerLiveEvent) => void
): IDesignerStreamHandle {
  let running: boolean = true;
  let abortController: AbortController | null = null;

  const run = async (): Promise<void> => {
    const url: string = `${CommonHelperFunctions.GetBackendUrl()}/designer/events/stream?clientId=${encodeURIComponent(clientId)}`;
    while (running) {
      const controller: AbortController = new AbortController();
      abortController = controller;
      try {
        await StreamingHelperService.streamEvents(
          url,
          (data: unknown) => {
            if (isDesignerLiveEvent(data)) onEvent(data);
          },
          controller.signal
        );
      } catch {
        // network/stream error — fall through to the reconnect delay
      }
      if (!running) break;
      await new Promise<void>((resolve: () => void) => setTimeout(resolve, RECONNECT_DELAY_MS));
    }
  };

  void run();

  return {
    stop: (): void => {
      running = false;
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
    }
  };
}
